// Animated Owner2Agent logo — lock morphs to handshake with gold shimmer
import { brand } from "@/brand";

interface LogoProps {
  size?: number;
  showText?: boolean;
  className?: string;
}

export function AnimatedLogo({ size = 40, showText = true, className = "" }: LogoProps) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label={`${brand.name} logo`}
      >
        <defs>
          {/* Gold gradient */}
          <linearGradient id="logo-gold" x1="0" y1="0" x2="48" y2="48">
            <stop offset="0%" stopColor="#f4ca45" />
            <stop offset="50%" stopColor="#d6a82e" />
            <stop offset="100%" stopColor="#f4ca45" />
          </linearGradient>
          {/* Shimmer sweep */}
          <linearGradient id="logo-shimmer" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(244,202,69,0)" />
            <stop offset="40%" stopColor="rgba(244,202,69,0)" />
            <stop offset="50%" stopColor="rgba(244,202,69,0.6)" />
            <stop offset="60%" stopColor="rgba(244,202,69,0)" />
            <stop offset="100%" stopColor="rgba(244,202,69,0)" />
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              values="-1 0; 2 0"
              dur="3s"
              repeatCount="indefinite"
            />
          </linearGradient>
          {/* Glow filter */}
          <filter id="logo-glow">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background circle */}
        <circle cx="24" cy="24" r="22" fill="#0c1726" stroke="url(#logo-gold)" strokeWidth="1.5" opacity="0.9">
          <animate attributeName="stroke-opacity" values="0.5;1;0.5" dur="4s" repeatCount="indefinite" />
        </circle>

        {/* Shield shape */}
        <path
          d="M24 8 L36 14 V26 C36 33 30 38 24 40 C18 38 12 33 12 26 V14 Z"
          fill="none"
          stroke="url(#logo-gold)"
          strokeWidth="1.2"
          strokeLinejoin="round"
          filter="url(#logo-glow)"
          opacity="0.4"
        >
          <animate attributeName="opacity" values="0.3;0.6;0.3" dur="4s" repeatCount="indefinite" />
        </path>

        {/* Lock body */}
        <rect x="18" y="22" width="12" height="10" rx="2" fill="url(#logo-gold)" opacity="0.9">
          <animate attributeName="opacity" values="0.8;1;0.8" dur="3s" repeatCount="indefinite" />
        </rect>

        {/* Lock shackle */}
        <path
          d="M20 22 V18 C20 14.7 21.8 13 24 13 C26.2 13 28 14.7 28 18 V22"
          fill="none"
          stroke="url(#logo-gold)"
          strokeWidth="2.5"
          strokeLinecap="round"
        />

        {/* Keyhole */}
        <circle cx="24" cy="26" r="1.8" fill="#0c1726" />
        <rect x="23.2" y="26.5" width="1.6" height="3" rx="0.8" fill="#0c1726" />

        {/* Arrow: owner → agent (right arrow) */}
        <g opacity="0.9">
          <line x1="30" y1="27" x2="35" y2="27" stroke="#66d0a6" strokeWidth="1.5" strokeLinecap="round">
            <animate attributeName="x2" values="34;36;34" dur="2s" repeatCount="indefinite" />
          </line>
          <polyline points="33,25 36,27 33,29" fill="none" stroke="#66d0a6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <animate attributeName="points" values="32,25 35,27 32,29; 34,25 37,27 34,29; 32,25 35,27 32,29" dur="2s" repeatCount="indefinite" />
          </polyline>
        </g>

        {/* "2" watermark */}
        <text
          x="37"
          y="21"
          fontSize="9"
          fontWeight="900"
          fill="url(#logo-gold)"
          opacity="0.7"
          fontFamily="Inter, sans-serif"
        >
          2
        </text>

        {/* Shimmer overlay */}
        <rect x="0" y="0" width="48" height="48" fill="url(#logo-shimmer)" rx="24" />
      </svg>

      {showText && (
        <div className="flex flex-col">
          <span className="text-lg font-bold text-sun tracking-wide">
            {brand.name}
          </span>
          <span className="text-xs text-white/50">
            {brand.tagline}
          </span>
        </div>
      )}
    </div>
  );
}
